/**
 * Theme per organization - resolved by organization name from localStorage.
 * Reads user.organization.name or super_admin_organization_name (when set by org selector).
 */

import { normalizeOrgName } from "../branding/org-normalize.util";

export const AVAILABLE_THEMES = [
    "theme-default",
    "theme-brand",
    "theme-teal",
    "theme-rose",
    "theme-purple",
    "theme-amber",
] as const;

export type ThemeId = (typeof AVAILABLE_THEMES)[number];

const DEFAULT_THEME: ThemeId = "theme-brand";

/** Map normalized organization name -> Fuse theme id. No org IDs. */
export const ORGANIZATION_THEME_MAP: Record<string, ThemeId> = {
    appcelerate: "theme-brand",
    "appcelerate-dev": "theme-brand",
    "appcelerate-latam": "theme-brand",
    "appcelerate latam": "theme-brand",
};

function getThemeForOrgName(orgName: string | null | undefined): ThemeId | null {
    const key = normalizeOrgName(orgName);
    if (!key) return null;
    return ORGANIZATION_THEME_MAP[key] ?? null;
}

/**
 * Returns the Fuse theme id for the current organization.
 * Resolved by organization name: super_admin_organization_name or user.organization.name.
 */
export function getThemeForCurrentOrganization(): ThemeId {
    if (typeof localStorage === "undefined") return DEFAULT_THEME;

    const superAdminName = localStorage.getItem("super_admin_organization_name");
    if (superAdminName) {
        const theme = getThemeForOrgName(superAdminName);
        if (theme) return theme;
    }

    try {
        const raw = localStorage.getItem("user");
        if (!raw) return DEFAULT_THEME;
        const user = JSON.parse(raw);
        const orgName = user?.organization?.name;
        const theme = getThemeForOrgName(orgName);
        return theme ?? DEFAULT_THEME;
    } catch {
        return DEFAULT_THEME;
    }
}
